import * as React from "react";

type BeforeInstallPromptEvent = Event & {
  prompt: () => Promise<void>;
  userChoice: Promise<{ outcome: "accepted" | "dismissed"; platform: string }>;
};

function isIosUa(): boolean {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent;
  return /iPad|iPhone|iPod/.test(ua) || (navigator.platform === "MacIntel" && navigator.maxTouchPoints > 1);
}

/**
 * Real iOS Safari install path (Share → Home Screen).
 * Chromium (incl. Playwright Desktop Chrome with iPhone UA) exposes
 * `BeforeInstallPromptEvent` — those must NOT see the iOS Share copy (#21).
 * E2E may set `sessionStorage['pwa-force-ios-install-hint']=1` to force the hint path.
 */
export function isIosSafariInstallPath(): boolean {
  if (typeof window === "undefined" || typeof navigator === "undefined") return false;
  try {
    if (sessionStorage.getItem("pwa-force-ios-install-hint") === "1") return true;
  } catch {
    /* ignore */
  }
  if (!isIosUa()) return false;
  const w = window as unknown as { BeforeInstallPromptEvent?: unknown };
  if (typeof w.BeforeInstallPromptEvent !== "undefined") return false;
  if (/CriOS|FxiOS|OPiOS|EdgiOS/.test(navigator.userAgent)) return false;
  return true;
}

function isStandalone(): boolean {
  if (typeof window === "undefined") return false;
  const nav = window.navigator as Navigator & { standalone?: boolean };
  return window.matchMedia("(display-mode: standalone)").matches || nav.standalone === true;
}

export function useInstallPrompt() {
  const [deferred, setDeferred] = React.useState<BeforeInstallPromptEvent | null>(null);
  const [installed, setInstalled] = React.useState(() => isStandalone());
  const iosSafari = isIosSafariInstallPath();

  React.useEffect(() => {
    const onBip = (e: Event) => {
      e.preventDefault();
      setDeferred(e as BeforeInstallPromptEvent);
    };
    const onInstalled = () => {
      setInstalled(true);
      setDeferred(null);
    };
    window.addEventListener("beforeinstallprompt", onBip);
    window.addEventListener("appinstalled", onInstalled);
    return () => {
      window.removeEventListener("beforeinstallprompt", onBip);
      window.removeEventListener("appinstalled", onInstalled);
    };
  }, []);

  const promptInstall = React.useCallback(async () => {
    if (!deferred) return false;
    await deferred.prompt();
    const choice = await deferred.userChoice;
    setDeferred(null);
    return choice.outcome === "accepted";
  }, [deferred]);

  const canPrompt = !!deferred && !installed;
  const showIosHint = iosSafari && !installed;

  return {
    canPrompt,
    showIosHint,
    installed,
    /** Banner should render when BIP CTA or real iOS hint is available. */
    readyToShow: canPrompt || showIosHint,
    promptInstall
  };
}
